import { useEffect, useMemo } from 'react';
import { addMonths } from 'date-fns';
import { useAuth } from '@shared/components/AuthProvider';
import { useFeatureValue } from '@shared/core/featureFlags';
import { getTimezone } from '@shared/createEvent/utils/getTimezone';
import { createAndAttachPixelImage } from '@shared/createEvent/utils/pixelImage';
import { useSegmentGroupCallForEvent } from '@shared/core';
import { AcquisitionContext, CreateEventMutation, EventType, useTrackImpactRadiusMutation } from '@graphql/generated';

import { omitEmpty } from '@shared/createEvent/utils/omitEmpty';
import { useCreateEventState } from '@shared/createEvent/CreateEventState';
import { useDesignParams } from '@shared/createEvent/hooks/useDesignParams';
import { getEventTypeConfig } from '@config';
import { getCreateEventPayload } from '@shared/utils/impactRadius/utilsImpactRadius';
import { readAndResetAcquisitionContexts } from '../acquisitionContextTracking';

export interface Location {
  place: string;
  placeId?: string;
  lat?: number;
  lng?: number;
}

export type CreateEventValuesProps = {
  eventType: EventType;
  eventDate: Date | null;
  ownerFirstName?: string;
  ownerLastName?: string;
  eventDisplayName?: string;
  partnerFirstName?: string;
  location?: Location | null;
  font?: string;
  template?: string;
  scenario?: string;
  theme?: string;
  acquisitionContext?: AcquisitionContext;
};

export type CreateEventTelemetryProps = {
  trackError: (
    action: string,
    error: unknown
  ) => {
    action: string;
    actionType: 'error';
    category: string;
    extraInfo: {
      error: unknown;
    };
  };
  dateSet: (
    eventDate: string,
    dateLabel: string,
    durationDays: number | undefined
  ) => {
    action: string;
    category: string;
    label: string;
    extraInfo: {
      eventDate: string;
      durationDays: number | undefined;
    };
  };
  timezoneRegistered: (info: {
    timezoneId: string;
    timezoneMethod: string;
  }) => {
    action: string;
    category: string;
    extraInfo: {
      timezoneId: string;
      timezoneMethod: string;
    };
  };
  submitDetailsClicked: (
    userEmail: string
  ) => {
    action: string;
    actionType: 'click';
    label: string;
    category: string;
    extraInfo: {
      email: string;
      CtaName: string;
    };
  };
  joyEventCreated: (
    website: string,
    eventId: string,
    email?: string | undefined
  ) => {
    action: string;
    label: string;
    category: string;
    extraInfo: {
      eventId: string;
      email: string | undefined;
      orderId: number;
    };
  };
};

export type CreateEventProps = {
  values: CreateEventValuesProps;
  telemetry: CreateEventTelemetryProps;
};

/**
 * TODO: Consolidate baby registry + wedding create controllers
 */
export const useCreateEvent = () => {
  const authProvider = useAuth();
  const { timezoneId, timezoneMethod } = getTimezone();
  const { createJoyEvent, addEventCreatedListener } = useCreateEventState();
  const { settled } = useDesignParams();
  const [trackImpactRadius] = useTrackImpactRadiusMutation();
  const enableBlankDates = useFeatureValue('enableBlankDates');
  const { executeSegmentGroupCallForEvent } = useSegmentGroupCallForEvent();

  const waitForDesignDeferred = useMemo(() => {
    let outsideResolve: (value?: unknown) => void = () => {};
    const promise = new Promise(resolve => {
      outsideResolve = resolve;
    });
    return { promise, resolve: outsideResolve };
  }, []);

  useEffect(() => {
    if (settled) {
      waitForDesignDeferred.resolve();
    }
  }, [settled, waitForDesignDeferred]);

  const handleTrackImpactRadius = async (event: CreateEventMutation, cb: (e: unknown) => void) => {
    try {
      const eventTypeConfig = getEventTypeConfig(event.createEventV0301?.info.eventType ?? EventType.general);
      const payload = getCreateEventPayload(
        eventTypeConfig.impactRadiusCampaignId,
        eventTypeConfig.impactRadiusCreateEventActionTrackerId,
        event.createEventV0301?.id,
        authProvider.currentUser.profile?.activeAlias?.auth0Id
      );

      if (!payload) {
        return;
      }

      await trackImpactRadius({ variables: { payload } });
    } catch (e) {
      cb(e);
    }
  };

  const getTelemetryDateLabel = (date: Date | null) => {
    if (!date) {
      return 'dateUndecided';
    }

    if (date.toDateString() === new Date().toDateString()) {
      return 'today';
    } else if (date.toDateString() === addMonths(new Date(), 9).toDateString()) {
      return 'default';
    } else {
      return 'custom';
    }
  };

  const handleCreateEvent = async ({ values, telemetry }: CreateEventProps) => {
    const eventDate = values.eventDate;
    const eventDateToIsoString = eventDate?.toISOString() || null;
    const durationDays = eventDate ? Math.ceil((eventDate.getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24)) : undefined;

    telemetry.dateSet(eventDateToIsoString ?? 'dateUndecided', getTelemetryDateLabel(eventDate), durationDays);
    telemetry.timezoneRegistered({ timezoneId, timezoneMethod });
    telemetry.submitDetailsClicked(authProvider.currentUser?.profile?.email!);

    await waitForDesignDeferred.promise;

    createJoyEvent({
      variables: {
        payload: omitEmpty({
          eventType: values.eventType,
          ownerFirstName: values.ownerFirstName,
          fianceeFirstName: values.partnerFirstName,
          ownerLastName: values.ownerLastName,
          fianceeLastName: '',
          eventDisplayName: values.eventDisplayName,
          lat: values.location?.lat,
          lng: values.location?.lng,
          placeId: values.location?.placeId,
          placeName: values.location?.place,
          font: 'Lato',
          template: values.template,
          mainTzid: timezoneId,
          theme: values.theme,
          mainDate: enableBlankDates ? eventDateToIsoString : eventDateToIsoString || addMonths(new Date(), 9).toISOString(),
          acquisitionContext: {
            currentTimestamp: Date.now(),
            details: readAndResetAcquisitionContexts()
          }
        })
      }
    });

    createAndAttachPixelImage({ action: 'lead' });
    addEventCreatedListener(async event => {
      if (event.createEventV0301) {
        const { id: eventId, firebaseId, website, info } = event.createEventV0301!;
        /**
         * Must call this before future telemetry calls -- this sets the `joyEventType` group property
         */
        executeSegmentGroupCallForEvent(firebaseId, {
          postgresEventId: eventId,
          eventType: info.eventType
        });
        telemetry.joyEventCreated(website, firebaseId, authProvider.currentUser.profile?.email ?? '');
        handleTrackImpactRadius(event, e => {
          telemetry.trackError('CreateEventTrackImpactRadius', e);
        });
      }
    });
  };

  return { getTelemetryDateLabel, handleTrackImpactRadius, handleCreateEvent };
};
